/**
 * Shared filesystem utilities for LocalFilesystem and LocalSkillSource.
 *
 * These utilities provide consistent implementations for common fs operations.
 */

import * as fs from 'node:fs/promises';
import * as os from 'node:os';
import * as path from 'node:path';

import { FileNotFoundError } from '../errors';

// =============================================================================
// Tilde Expansion
// =============================================================================

/**
 * Expand a leading `~` or `~/` to the user's home directory.
 * Shell commands handle this automatically, but Node.js path APIs do not.
 */
export function expandTilde(p: string): string {
  if (p === '~') return os.homedir();
  if (p.startsWith('~/') || p.startsWith('~\\')) {
    return path.join(os.homedir(), p.slice(2));
  }
  return p;
}

// =============================================================================
// Types
// =============================================================================

/**
 * Full file stat information.
 * Used by both WorkspaceFilesystem and SkillSource.
 */
export interface FsStatResult {
  /** File or directory name */
  name: string;
  /** 'file' or 'directory' */
  type: 'file' | 'directory';
  /** Size in bytes (0 for directories) */
  size: number;
  /** Creation time */
  createdAt: Date;
  /** Last modification time */
  modifiedAt: Date;
  /** MIME type (for files) */
  mimeType?: string;
}

// =============================================================================
// Error Utilities
// =============================================================================

/**
 * Check if an error is an ENOENT (file not found) error.
 */
export function isEnoentError(error: unknown): error is NodeJS.ErrnoException & { code: 'ENOENT' } {
  return (
    error !== null && typeof error === 'object' && 'code' in error && (error as NodeJS.ErrnoException).code === 'ENOENT'
  );
}

/**
 * Check if an error is an EEXIST (file exists) error.
 */
export function isEexistError(error: unknown): error is NodeJS.ErrnoException & { code: 'EEXIST' } {
  return (
    error !== null && typeof error === 'object' && 'code' in error && (error as NodeJS.ErrnoException).code === 'EEXIST'
  );
}

// =============================================================================
// MIME Type Detection
// =============================================================================

const MIME_TYPES: Record<string, string> = {
  // Text
  txt: 'text/plain',
  html: 'text/html',
  htm: 'text/html',
  css: 'text/css',
  csv: 'text/csv',
  md: 'text/markdown',
  // Code
  js: 'application/javascript',
  mjs: 'application/javascript',
  ts: 'application/typescript',
  tsx: 'application/typescript',
  jsx: 'application/javascript',
  json: 'application/json',
  xml: 'application/xml',
  yaml: 'text/yaml',
  yml: 'text/yaml',
  // Programming languages
  py: 'text/x-python',
  rb: 'text/x-ruby',
  go: 'text/x-go',
  rs: 'text/x-rust',
  java: 'text/x-java',
  c: 'text/x-c',
  cpp: 'text/x-c++',
  h: 'text/x-c',
  hpp: 'text/x-c++',
  sh: 'text/x-sh',
  bash: 'text/x-sh',
  zsh: 'text/x-sh',
  // Config
  toml: 'text/toml',
  ini: 'text/plain',
  env: 'text/plain',
  // Database/Query
  sql: 'text/x-sql',
  graphql: 'application/graphql',
  gql: 'application/graphql',
  // Frameworks
  vue: 'text/x-vue',
  // Images
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  webp: 'image/webp',
  ico: 'image/x-icon',
  bmp: 'image/bmp',
  tiff: 'image/tiff',
  tif: 'image/tiff',
  heic: 'image/heic',
  heif: 'image/heif',
  avif: 'image/avif',
  // Documents
  pdf: 'application/pdf',
  // Audio
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  flac: 'audio/flac',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  // Video
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
  // Archives
  zip: 'application/zip',
  tar: 'application/x-tar',
  gz: 'application/gzip',
  tgz: 'application/gzip',
  bz2: 'application/x-bzip2',
  '7z': 'application/x-7z-compressed',
  rar: 'application/vnd.rar',
  // Executables / binaries
  exe: 'application/vnd.microsoft.portable-executable',
  dll: 'application/vnd.microsoft.portable-executable',
  so: 'application/x-sharedlib',
  dylib: 'application/x-sharedlib',
  bin: 'application/x-binary',
  dat: 'application/x-binary',
  // Disk images / packages
  dmg: 'application/x-apple-diskimage',
  iso: 'application/x-iso9660-image',
  deb: 'application/vnd.debian.binary-package',
  rpm: 'application/x-rpm',
  // Office documents
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // Fonts
  ttf: 'font/ttf',
  otf: 'font/otf',
  woff: 'font/woff',
  woff2: 'font/woff2',
  // Compiled code
  wasm: 'application/wasm',
  class: 'application/java-vm',
  pyc: 'application/x-python-code',
};

/**
 * Get MIME type for a filename based on extension.
 */
export function getMimeType(filename: string): string {
  const ext = path.extname(filename).slice(1).toLowerCase();
  return MIME_TYPES[ext] ?? 'application/octet-stream';
}

/**
 * Extensions that should be treated as text files.
 */
const TEXT_EXTENSIONS = new Set([
  '.md',
  '.txt',
  '.json',
  '.yaml',
  '.yml',
  '.js',
  '.mjs',
  '.ts',
  '.tsx',
  '.jsx',
  '.py',
  '.rb',
  '.go',
  '.rs',
  '.java',
  '.c',
  '.cpp',
  '.h',
  '.hpp',
  '.sh',
  '.bash',
  '.zsh',
  '.html',
  '.htm',
  '.css',
  '.xml',
  '.toml',
  '.ini',
  '.env',
  '.csv',
  '.sql',
  '.graphql',
  '.gql',
  '.vue',
  '.svg',
]);

/**
 * Check if a file should be treated as text based on extension.
 */
export function isTextFile(filename: string): boolean {
  const ext = path.extname(filename).toLowerCase();
  return TEXT_EXTENSIONS.has(ext);
}

// =============================================================================
// Path Resolution
// =============================================================================

/**
 * Resolve a path against a base directory.
 *
 * - Tilde (`~`) is expanded to the user's home directory.
 * - Absolute paths are normalized and returned as-is.
 * - Relative paths (including `../`) are resolved against `basePath`.
 *
 * @param basePath - The absolute base path to resolve against
 * @param filePath - The path to resolve
 * @returns The absolute resolved path
 */
export function resolveToBasePath(basePath: string, filePath: string): string {
  const expanded = expandTilde(filePath);
  if (path.isAbsolute(expanded)) {
    return path.normalize(expanded);
  }
  return path.resolve(basePath, expanded);
}

// =============================================================================
// Filesystem Operations
// =============================================================================

/**
 * Check if a path exists.
 * Never throws - returns false on any error.
 *
 * @param absolutePath - The absolute path to check
 * @returns true if path exists and is accessible
 */
export async function fsExists(absolutePath: string): Promise<boolean> {
  try {
    await fs.access(absolutePath);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get file/directory stats.
 * Throws FileNotFoundError if path doesn't exist.
 *
 * @param absolutePath - The absolute path to stat
 * @param userPath - The user-facing path for error messages
 * @returns File stat information
 * @throws {FileNotFoundError} if path doesn't exist
 */
export async function fsStat(absolutePath: string, userPath: string): Promise<FsStatResult> {
  try {
    const stats = await fs.stat(absolutePath);
    return {
      name: path.basename(absolutePath),
      type: stats.isDirectory() ? 'directory' : 'file',
      size: stats.size,
      createdAt: stats.birthtime,
      modifiedAt: stats.mtime,
      mimeType: stats.isFile() ? getMimeType(absolutePath) : undefined,
    };
  } catch (error: unknown) {
    if (isEnoentError(error)) {
      throw new FileNotFoundError(userPath);
    }
    throw error;
  }
}
